/**
 * Programs — the entitlement layer's admin page (question-pools-and-entitlement/question-pools-and-programs-target.md §3 L4).
 *
 * A Program is one rule: IF a student's profile matches `applies` (their class +
 * target exam), THEN they receive `grants` (board-grade pools, optionally lensed
 * pools). Resolution is match-all-and-union — a student can match many
 * programs; their grants combine. This page manages the rows; resolution lives
 * in the backend (shared/curriculum/entitlement.ts).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { programsApi } from '../api/programsApi';
import type { Program, ProgramGrant, ProgramListItem } from '../types';
import { GRADE_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS, gradesForProgramme } from '@padcanvas/shared';
import { Modal, Input, Select, Button } from '@shared-ui/components';

const EXAM_TRACKS = ['JEE', 'NEET'];

const grantLabel = (g: ProgramGrant): string =>
  `${g.board.toLowerCase()}${g.grade ? `:${g.grade}` : ''}`;

const appliesLabel = (p: Program): string => {
  const a = p.applies;
  if (!a || (!a.grade && !a.targetExam)) return '— never auto-matches —';
  return [a.grade ? `class ${a.grade}` : null, a.targetExam ?? null].filter(Boolean).join(' + ');
};

export function Programs() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<Partial<Program> | null>(null);
  const [isNew, setIsNew] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['programs'], queryFn: programsApi.listPrograms });
  const programs = (data?.programs ?? []) as ProgramListItem[];

  const save = useMutation({
    mutationFn: (p: Partial<Program>) => programsApi.saveProgram(p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['programs'] }); setModal(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => programsApi.deleteProgram(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['programs'] }); setModal(null); },
  });

  const setGrant = (i: number, patch: Partial<ProgramGrant>) => {
    if (!modal) return;
    const grants = [...(modal.grants ?? [])];
    grants[i] = { ...grants[i], ...patch };
    setModal({ ...modal, grants });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Programs</h2>
        <Button onClick={() => { setIsNew(true); setModal({ isActive: true, grants: [] }); }}>New program</Button>
      </div>
      <p style={{ color: 'var(--text-secondary, #667)', maxWidth: 760, marginTop: 4 }}>
        Each program is one entitlement rule: students whose profile matches <b>applies</b> receive the
        <b> grants</b> (board-grade question pools). A student can match several programs — grants combine.
        Deactivating a row reverts its effect immediately.
      </p>

      {isLoading ? <p>Loading…</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color, #dde)' }}>
              <th style={{ padding: 8 }}>Id</th>
              <th style={{ padding: 8 }}>Name</th>
              <th style={{ padding: 8 }}>Applies (student profile)</th>
              <th style={{ padding: 8 }}>Grants (pools)</th>
              <th style={{ padding: 8 }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {programs.map(p => (
              <tr key={p.id}
                  onClick={() => { setIsNew(false); setModal(structuredClone(p)); }}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-color, #eef)', opacity: p.isActive === false ? 0.5 : 1 }}>
                <td style={{ padding: 8 }}><code>{p.id}</code></td>
                <td style={{ padding: 8 }}>{p.name}</td>
                <td style={{ padding: 8 }}>{appliesLabel(p)}</td>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{p.grants.map(grantLabel).join(', ') || '—'}</td>
                <td style={{ padding: 8 }}>{p.isActive === false ? 'inactive' : '✓'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={isNew ? 'New Program' : `Edit ${modal?.id}`} size="md">
        {modal && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label>Id (stable key; lowercase + dashes)
              <Input value={modal.id ?? ''} disabled={!isNew}
                     onChange={(e) => setModal({ ...modal, id: e.target.value })} placeholder="jee-foundation-9" />
            </label>
            <label>Name
              <Input value={modal.name ?? ''} onChange={(e) => setModal({ ...modal, name: e.target.value })}
                     placeholder="JEE Foundation — Class 9" />
            </label>

            <fieldset style={{ border: '1px solid var(--border-color, #dde)', borderRadius: 6, padding: 12 }}>
              <legend>Applies — which students (empty = never auto-matches)</legend>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ flex: 1 }}>Student class
                  <Select value={modal.applies?.grade ?? ''}
                          onChange={(e) => setModal({ ...modal, applies: { ...modal.applies, grade: (e.target.value || undefined) as NonNullable<Program['applies']>['grade'] } })}>
                    <option value="">any</option>
                    {GRADE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </label>
                <label style={{ flex: 1 }}>Target exam on profile
                  <Select value={modal.applies?.targetExam ?? ''}
                          onChange={(e) => setModal({ ...modal, applies: { ...modal.applies, targetExam: (e.target.value || undefined) as NonNullable<Program['applies']>['targetExam'] } })}>
                    <option value="">any</option>
                    {REGISTRABLE_PROGRAMME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </label>
              </div>
            </fieldset>

            <fieldset style={{ border: '1px solid var(--border-color, #dde)', borderRadius: 6, padding: 12 }}>
              <legend>Grants — which pools they get</legend>
              {(modal.grants ?? []).map((g, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'end', marginBottom: 8 }}>
                  <label style={{ flex: 1 }}>Board / track
                    <Select value={g.board ?? ''} onChange={(e) => setGrant(i, { board: e.target.value as ProgramGrant['board'], ...(EXAM_TRACKS.includes(e.target.value) || (g.grade && !gradesForProgramme(e.target.value).includes(g.grade)) ? { grade: undefined } : {}) })}>
                      <option value="">— select —</option>
                      {REGISTRABLE_PROGRAMME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </label>
                  <label style={{ flex: 1 }}>Pool grade
                    <Select value={g.grade ?? ''} disabled={EXAM_TRACKS.includes(g.board)}
                            onChange={(e) => setGrant(i, { grade: (e.target.value || undefined) as ProgramGrant['grade'] })}>
                      <option value="">{EXAM_TRACKS.includes(g.board) ? '(gradeless)' : '— select —'}</option>
                      {gradesForProgramme(g.board).map(c => <option key={c} value={c}>{c}</option>)}
                    </Select>
                  </label>
                  <Button variant="secondary" onClick={() => setModal({ ...modal, grants: (modal.grants ?? []).filter((_, j) => j !== i) })}>✕</Button>
                </div>
              ))}
              <Button variant="secondary" onClick={() => setModal({ ...modal, grants: [...(modal.grants ?? []), { board: 'CBSE' as ProgramGrant['board'] }] })}>
                + Add grant
              </Button>
            </fieldset>

            <label>Description
              <Input value={modal.description ?? ''} onChange={(e) => setModal({ ...modal, description: e.target.value })} />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={modal.isActive !== false}
                     onChange={(e) => setModal({ ...modal, isActive: e.target.checked })} />
              Active
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {!isNew ? (
                <Button variant="danger" onClick={() => modal.id && window.confirm(`Delete program ${modal.id}?`) && remove.mutate(modal.id)}>
                  Delete
                </Button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
                <Button onClick={() => save.mutate(modal)} disabled={save.isPending || !modal.id || !modal.name}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            {(save.error || remove.error) && (
              <p style={{ color: 'var(--color-error)' }}>{String((save.error ?? remove.error as Error).message ?? '')}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
